import get from 'lodash.get';
import mapValues from 'lodash.mapvalues';

import { AppState } from 'services/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatDropdownOptions from 'utils/format-dropdown-options';

// @ts-ignore
import { selectCommodities } from 'services/commodities/selectors';

import { VarietyId, VarietiesState, VarietiesProps } from './types';

const selectVarieties = (state: AppState): VarietiesState => state.varieties;
const selectVarietyId = (_state: AppState, props: VarietiesProps): VarietyId =>
    props.varietyId;

const selectVarietiesArray = createDeepEqualSelector(
    selectVarieties,
    (varieties) => Object.values(varieties)
);

/**
 * Format varieties so that when a variety doesn't have a name it uses the name of it's commodity name.
 */
const selectFormattedVarieties = createDeepEqualSelector(
    [selectVarieties, selectCommodities],
    (varieties, commodities) =>
        mapValues(varieties, (variety) => {
            const { name, commodityId } = variety;
            const commodityName = get(commodities, [commodityId, 'name']);
            return {
                ...variety,
                name: name || commodityName
            };
        })
);

const selectVarietyOptions = createDeepEqualSelector(
    selectFormattedVarieties,
    (formattedVarieties) =>
        formatDropdownOptions(formattedVarieties, {
            keyField: 'id',
            textField: 'name'
        })
);

const selectVariety = createDeepEqualSelector(
    [selectVarieties, selectVarietyId],
    (varietiesState, varietyId) => get(varietiesState, varietyId)
);

const selectVarietyName = createDeepEqualSelector(selectVariety, (variety) =>
    get(variety, 'name')
);

const makeSelectVarietyName = (): typeof selectVarietyName => selectVarietyName;

const selectVarietyShortName = createDeepEqualSelector(
    selectVariety,
    (variety) => get(variety, 'shortName')
);

const makeSelectVarietyShortName = (): typeof selectVarietyShortName =>
    selectVarietyShortName;

export {
    selectVarietyId,
    selectVarieties,
    selectVarietiesArray,
    selectFormattedVarieties,
    selectVarietyOptions,
    selectVariety,
    selectVarietyName,
    makeSelectVarietyName,
    selectVarietyShortName,
    makeSelectVarietyShortName
};
